// The interactor provides a centralized way for managing all interactions
// between things: attacking, defending, healing, etc.
//
// Usage:
//     // Once per tick
//     interactor.handleAllInteractions(dt, time);

import {System} from "detect-collisions";
import {Thing, THING_TYPES} from "./i.js"; // ./thing.js

export class Interactor {
	// Create a new Interactor
	// In the future, this constructor may set which enemies are friendly, neutral, or
	// hostile towards the player or other enemies, if that's something that I will want
	// to change dynamically.
	// Args:
	//   space - reference to space
	constructor(space) {
		this.space = space;

		this._hitboxes = new Set();
		this._collisionSystem = new System();

		// Map from `${lesserId}-${greaterId}` to cooldown in seconds
		this._collisionCooldowns = new Map();

		// Map of all interaction pairs
		// Don't create mirrored pairs here - for example, if you created the
		// "player-station" pair, then you don't need a "station-player" pair.
		// If some pair is not present, then those two types of objects don't
		// interact.
		this._iPairs = new Map([
			[
				`${THING_TYPES.BULLET}-${THING_TYPES.STATION}`,
				this._iBulletStation
			],
			[
				`${THING_TYPES.BULLET}-${THING_TYPES.ASTEROID}`,
				this._iBulletAsteroid
			],
			[
				`${THING_TYPES.BULLET}-${THING_TYPES.METEORUS}`,
				// Reuse existing function
				this._iBulletAsteroid
			],
			[
				`${THING_TYPES.BULLET}-${THING_TYPES.SPACESHIP}`,
				// Reuse existing function
				this._iBulletAsteroid
			],
			[
				`${THING_TYPES.PLAYER}-${THING_TYPES.STATION}`,
				this._iPlayerStation
			],
			[
				`${THING_TYPES.PLAYER}-${THING_TYPES.ASTEROID}`,
				this._iPlayerAsteroid
			],
			[
				`${THING_TYPES.STATION}-${THING_TYPES.ASTEROID}`,
				this._iStationAsteroid
			],
			[
				`${THING_TYPES.SPACESHIP}-${THING_TYPES.ASTEROID}`,
				// Reuse existing function
				this._iPlayerAsteroid
			],
			[
				`${THING_TYPES.STATION}-${THING_TYPES.METEORUS}`,
				// Reuse the existing function
				this._iStationAsteroid
			],
			[
				`${THING_TYPES.STATION}-${THING_TYPES.SPACESHIP}`,
				// Reuse existing function
				this._iStationAsteroid
			],
			[
				`${THING_TYPES.SIZY_WEAPON}-${THING_TYPES.STATION}`,
				// Reuse existing function
				this._iBulletStation
			],
			[
				`${THING_TYPES.SIZY_WEAPON}-${THING_TYPES.ASTEROID}`,
				// Reuse existing function
				this._iBulletAsteroid
			],
			[
				`${THING_TYPES.PLAYER}-${THING_TYPES.SIZY_WEAPON}`,
				this._iPlayerBullet
			],
			[
				`${THING_TYPES.SIZY_WEAPON}-${THING_TYPES.METEORUS}`,
				// Reuse existing function
				this._iBulletAsteroid
			],
			[
				`${THING_TYPES.HEALING}-${THING_TYPES.STATION}`,
				this._iHealingThing
			],
			[
				`${THING_TYPES.HEALING}-${THING_TYPES.ASTEROID}`,
				this._iHealingThing
			],
			[
				`${THING_TYPES.HEALING}-${THING_TYPES.METEORUS}`,
				this._iHealingThing
			],
			[
				`${THING_TYPES.HEALING}-${THING_TYPES.PLAYER}`,
				this._iHealingThing
			],
			[
				`${THING_TYPES.HEALING}-${THING_TYPES.SPACESHIP}`,
				this._iHealingThing
			],
			[
				`*-${THING_TYPES.DROP}`,
				this._iThingDrop
			]
		]);
	}

	// Add a new hitbox to the interactor
	// Args:
	//   hitbox - a detect-collisions body
	addHitbox(hitbox) {
		this._hitboxes.add(hitbox);
		this._collisionSystem.insert(hitbox);
	}

	// Remove a hitbox from the interactor
	// Does nothing if the hitbox is not in the interactor.
	// Args:
	//   hitbox - a detect-collisions body
	removeHitbox(hitbox) {
		this._hitboxes.delete(hitbox);
		this._collisionSystem.remove(hitbox);
	}

	// Detect and handle all interactions between things
	// Args:
	//   dt   - time since last tick, in seconds
	//   time - total in-game time, in seconds
	handleAllInteractions(dt, time) {
		this._updateCooldowns(dt);

		this._hitboxes.forEach(hitbox => {
			let thing = hitbox.thing;
			hitbox
				.setPosition(thing.pos.x, thing.pos.y, false)
				.setAngle(thing.rot)
				.updateBody();
		});

		this._collisionSystem.checkAll(response =>
			this._interact(
				response.a.thing, response.b.thing,
				response.aInB, response.bInA,
				dt, time
			)
		);
	}

	// Update collision cooldowns
	// Call this each tick, before any _interact() calls.
	_updateCooldowns(dt) {
		this._collisionCooldowns.forEach((cooldown, key, map) => {
			if (cooldown <= dt) {
				map.delete(key);
			} else {
				map.set(key, cooldown - dt);
			}
		});
	}

	// Handle the interaction between thingA and thingB
	// Call this every tick for every pair of things that are colliding. Can be skipped
	// for very far-away things, for performance.
	// Args:
	//   thingA, thingB - colliding things; order doesn't matter, but don't call the
	//                    function twice with both orders
	//   aInB           - true iff thingA is contained within thingB
	//   BInA           - true iff thingB is contained within thingA
	//   dt             - time since last tick, in seconds
	//   time           - total in-game time, in seconds
	_interact(thingA, thingB, aInB, bInA, dt, time) {
		// If one of the things is already dead, the things can't interact
		if (thingA.state !== Thing.STATES.ALIVE
						|| thingB.state !== Thing.STATES.ALIVE) return;

		// If one of the things is not ready, the things can't interact
		// Most things don't have the "ready" property at all, so only check if that
		// property is explicitly set to false.
		if (thingA.ready === false || thingB.ready === false) return;

		let lesserId = Math.min(thingA.id, thingB.id);
		let greaterId = Math.max(thingA.id, thingB.id);
		let cooldownKey = `${lesserId}-${greaterId}`;
		if (this._collisionCooldowns.has(cooldownKey)) return;

		let typeA = thingA.TYPE;
		let typeB = thingB.TYPE;
		// Not using regex matching here for performance reasons
		if (this._iPairs.has(`${typeA}-${typeB}`)) {
			this._iPairs.get(`${typeA}-${typeB}`)
				.call(this, thingA, thingB, dt, time, aInB, bInA);
		} else if (this._iPairs.has(`${typeB}-${typeA}`)) {
			this._iPairs.get(`${typeB}-${typeA}`)
				.call(this, thingB, thingA, dt, time, bInA, aInB);
		} else if (this._iPairs.has(`*-${typeB}`)) {
			this._iPairs.get(`*-${typeB}`).call(this, thingA, thingB, dt, time,
							aInB, bInA);
		} else if (this._iPairs.has(`*-${typeA}`)) {
			this._iPairs.get(`*-${typeA}`).call(this, thingB, thingA, dt, time,
							bInA, aInB);
		}

		// Record new cooldown

		// If things are aren't both alive they won't collide again any time soon
		if (thingA.state !== Thing.STATES.ALIVE
						|| thingB.state !== Thing.STATES.ALIVE) return;
		// These things break from cooldowns
		// TODO: Actually, very few pairs of things truly _need_ to have their cooldowns
		// recorded, so a lot of optimization possible here. If two things don't affect
		// each other, no need to record a cooldown for them.
		const NO_COOLDOWN_TYPES = [
			THING_TYPES.PLAYER,
			THING_TYPES.STATION,
			THING_TYPES.DROP,
			THING_TYPES.HEALING
		];
		if (NO_COOLDOWN_TYPES.includes(typeA) && NO_COOLDOWN_TYPES.includes(typeB)) {
			return;
		}
		// Set a max size of this map, just in case :)
		// I am sure someone can use this max size to break the game.
		if (this._collisionCooldowns.size >= 2048) return;
		this._collisionCooldowns.set(cooldownKey, 1);
	}

	_iBulletStation(bullet, station, dt, time) {
		station.raiseShield();
		bullet.takeDamage(bullet.health, false, time);
	}

	_iBulletAsteroid(bullet, asteroid, dt, time) {
		let collisionStrength = Math.min(bullet.health, asteroid.health);
		bullet.takeDamage(collisionStrength, true, time);
		asteroid.takeDamage(collisionStrength, true, time);
	}

	_iPlayerStation(player, station, dt, time, aInB, bInA) {
		this.space.canDock = true;
		if (aInB) player.repair(dt, station.id);
	}

	_iPlayerAsteroid(player, asteroid, dt, time) {
		let collisionStrength = Math.min(player.health, asteroid.health, 10);
		player.takeDamage(collisionStrength, true, time);
		asteroid.takeDamage(collisionStrength, true, time);
	}

	_iPlayerBullet(player, bullet, dt, time) {
		let collisionStrength = Math.min(player.health, bullet.health);
		player.takeDamage(collisionStrength, true, time);
		bullet.takeDamage(collisionStrength, true, time);
	}

	_iStationAsteroid(station, asteroid, dt, time) {
		station.raiseShield();
		asteroid.takeDamage(asteroid.health, false, time);
	}

	_iHealingThing(healing, thing, dt, time) {
		if (!healing.isReadyToHeal()) return;
		thing.health = Math.min(thing.health + healing.health, thing.maxHealth);
		if (thing.healthBar) thing.healthBar.val = thing.health;
		healing.takeDamage(healing.health, true, time);
	}

	_iThingDrop(thing, drop, dt, time, aInB, bInA) {
		// TODO: Even if other things get the CAN_PICK_UP_ITEMS tag, the player
		// should still be the only one to pick up components
		if (!thing.CAN_PICK_UP_ITEMS) return;
		thing.receiveDrop(drop);
		drop.recordPickup();
	}
}
